import React, { useState } from 'react';
import { Button, Input } from '../common';

const ASPECT_TYPES = [
  { value: 'high_concept', label: 'High Concept', color: 'bg-primary-100 text-primary-800' },
  { value: 'trouble', label: 'Trouble', color: 'bg-red-100 text-red-800' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-800' },
];

export const CharacterAspects = ({
  aspects = [],
  onAdd,
  onRemove,
  canEdit = false,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newAspect, setNewAspect] = useState({ name: '', description: '', type: 'other' });

  const handleAdd = () => {
    if (newAspect.name.trim()) {
      onAdd(newAspect);
      setNewAspect({ name: '', description: '', type: 'other' });
      setIsAdding(false);
    }
  };

  const getTypeStyle = (type) => {
    return ASPECT_TYPES.find(t => t.value === type)?.color || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    return ASPECT_TYPES.find(t => t.value === type)?.label || 'Other';
  };

  // Sort aspects: high_concept first, then trouble, then others
  const sortedAspects = [...aspects].sort((a, b) => {
    const order = { high_concept: 0, trouble: 1, other: 2 };
    return (order[a.type] ?? 2) - (order[b.type] ?? 2);
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Aspects</h4>
        {canEdit && !isAdding && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsAdding(true)}
          >
            Add Aspect
          </Button>
        )}
      </div>

      {sortedAspects.length === 0 && !isAdding ? (
        <p className="text-sm text-gray-500 italic">No aspects defined.</p>
      ) : (
        <ul className="space-y-2">
          {sortedAspects.map((aspect, index) => (
            <li
              key={index}
              className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeStyle(aspect.type)}`}>
                    {getTypeLabel(aspect.type)}
                  </span>
                  <span className="font-medium text-gray-900">{aspect.name}</span>
                </div>
                {aspect.description && (
                  <p className="text-sm text-gray-500 mt-1">{aspect.description}</p>
                )}
              </div>

              {canEdit && onRemove && (
                <button
                  onClick={() => onRemove(aspects.indexOf(aspect))}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Remove aspect"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add aspect form */}
      {isAdding && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-3">
          <Input
            label="Aspect Name"
            value={newAspect.name}
            onChange={(e) => setNewAspect(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Reformed Assassin"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={newAspect.type}
              onChange={(e) => setNewAspect(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {ASPECT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={newAspect.description}
              onChange={(e) => setNewAspect(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of the aspect"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={!newAspect.name.trim()}
            >
              Add
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setIsAdding(false);
                setNewAspect({ name: '', description: '', type: 'other' });
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
